
import { useState, useEffect } from 'react';

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    if (!consentChecked) return;
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
  };

  const declineCookies = () => {
    localStorage.setItem('cookie-consent', 'declined');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center pointer-events-none">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm pointer-events-auto"></div>

      {/* Banner */}
      <div className="relative w-full max-w-6xl mx-4 mb-6 pointer-events-auto animate-slide-up">
        <div className="bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
              {/* Icon */}
              <div className="shrink-0">
                <div className="w-14 h-14 rounded-full bg-brand-green/10 flex items-center justify-center">
                  <i className="ri-cookie-line text-brand-green text-2xl"></i>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-brand-black mb-2">
                  Cookie-Hinweis & Datenschutz
                </h3>
                <p className="text-sm text-brand-grey leading-relaxed mb-3">
                  Wir verwenden ausschließlich <strong className="text-brand-black">technisch notwendige Cookies</strong>, 
                  die für den Betrieb dieser Website erforderlich sind. Es werden keine Tracking- oder Marketing-Cookies eingesetzt.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <p className="text-xs text-blue-900 leading-relaxed">
                    <i className="ri-information-line text-blue-600 mr-1"></i>
                    <strong>Wichtiger Hinweis:</strong> Diese Website wird über Readdy.ai bereitgestellt. 
                    Dabei werden Inhalte vorübergehend auf Servern in verschiedenen Ländern (u.a. USA, EU) 
                    zwischengespeichert, um optimale Ladezeiten zu gewährleisten.
                  </p>
                </div>

                {/* DSGVO Einwilligungs-Checkbox */}
                <label className="flex items-start gap-3 cursor-pointer group mb-3 select-none">
                  <div className="relative mt-0.5 shrink-0">
                    <input
                      type="checkbox"
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 rounded border-2 border-neutral-300 peer-checked:border-brand-green peer-checked:bg-brand-green transition-all flex items-center justify-center group-hover:border-brand-green/60">
                      {consentChecked && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-brand-grey leading-relaxed">
                    Ich habe die{' '}
                    <a
                      href="/datenschutz"
                      className="text-brand-green hover:text-brand-green-light underline cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Datenschutzerklärung
                    </a>{' '}
                    zur Kenntnis genommen und stimme der Verarbeitung meiner Daten gemäß DSGVO zu.
                  </span>
                </label>

                <p className="text-xs text-brand-grey">
                  Weitere Informationen finden Sie in unserer{' '}
                  <a
                    href="/datenschutz"
                    className="text-brand-green hover:text-brand-green-light underline cursor-pointer"
                  >
                    Datenschutzerklärung
                  </a>
                  .
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
                <button
                  onClick={declineCookies}
                  className="px-6 py-3 rounded-lg border border-neutral-300 text-brand-grey hover:bg-neutral-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  Nur notwendige
                </button>
                <button
                  onClick={acceptCookies}
                  disabled={!consentChecked}
                  className={`px-6 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    consentChecked
                      ? 'bg-brand-green text-white hover:bg-brand-green-light cursor-pointer'
                      : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                  }`}
                >
                  Akzeptieren & Bestätigen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
